import { Link, useNavigate } from 'react-router-dom';
import { Alert, Button, Input, QuietAddTrigger, QuietSelect } from '@shared/ui';
import { useDebounce, useIsMobile } from '@shared/hooks';
import type { ProductFiltersValues } from '@shared/components/ProductFilters';
import '@shared/styles/page.css';
import './ProductsPage.css';
import { IoCloudUploadOutline, IoLocationOutline, IoSearchOutline } from 'react-icons/io5';
import { Loader } from '@mantine/core';
import { useState, useMemo } from 'react';
import { ProductList, useProducts, AddToSetModal, AddToCategoryModal, CreateProductModal, EditProductModal, ImportProductsModal, type ProductFilters as ProductFiltersType } from '@modules/products';
import { usePickupPoints } from '@modules/pickup-points';
import { Breadcrumbs, BulkActionsSelect /* , FiltersPopover */ } from '@components';

export default function ProductsPage() {
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    const [isSelectionMode, setIsSelectionMode] = useState(false);
    const [selectedProductIds, setSelectedProductIds] = useState<Set<number>>(new Set());
    const [isAddToSetModalOpen, setIsAddToSetModalOpen] = useState(false);
    const [isAddToCategoryModalOpen, setIsAddToCategoryModalOpen] = useState(false);
    const [isCreateProductModalOpen, setIsCreateProductModalOpen] = useState(false);
    const [isImportModalOpen, setIsImportModalOpen] = useState(false);
    const [isEditProductModalOpen, setIsEditProductModalOpen] = useState(false);
    const [editingProductId, setEditingProductId] = useState<number | null>(null);
    const [modalProductIds, setModalProductIds] = useState<number[]>([]);

    // Фильтры
    const [filterValues] = useState<ProductFiltersValues>({
        colorIds: [],
        flowerTypeIds: [],
        assemblyTypeIds: [],
        minPrice: '',
        maxPrice: '',
        categoryId: null,
    });
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState<string>('popular');
    const [visibilityFilter, setVisibilityFilter] = useState<
        'all' | 'active' | 'hidden' | 'moderation' | 'rejected'
    >('all');
    const sortOptions = [
        { value: 'popular', label: 'По популярности' },
        { value: 'purchase-count', label: 'По количеству покупок' },
        { value: 'viewed-count', label: 'По количеству просмотров' },
        { value: 'rating-desc', label: 'По рейтингу' },
    ];
    const visibilityOptions = [
        { value: 'all' as const, label: 'Все товары' },
        { value: 'active' as const, label: 'Только активные' },
        { value: 'hidden' as const, label: 'Только скрытые' },
        { value: 'moderation' as const, label: 'На модерации' },
        { value: 'rejected' as const, label: 'Модерация отклонена' },
    ];

    const debouncedSearchQuery = useDebounce(searchQuery, 300);
    const debouncedMinPrice = useDebounce(filterValues.minPrice, 300);
    const debouncedMaxPrice = useDebounce(filterValues.maxPrice, 300);

    // Формируем объект фильтров
    const filters: ProductFiltersType = useMemo(() => {
        const result: ProductFiltersType = {};
        if (filterValues.colorIds.length > 0) result.colorIds = filterValues.colorIds;
        if (filterValues.flowerTypeIds.length > 0) result.flowerTypeIds = filterValues.flowerTypeIds;
        if (filterValues.assemblyTypeIds.length > 0) result.assemblyTypeIds = filterValues.assemblyTypeIds;
        if (filterValues.categoryId != null) result.categoryId = filterValues.categoryId;
        if (debouncedMinPrice.trim()) {
            const min = parseFloat(debouncedMinPrice);
            if (!isNaN(min)) result.minPrice = min;
        }
        if (debouncedMaxPrice.trim()) {
            const max = parseFloat(debouncedMaxPrice);
            if (!isNaN(max)) result.maxPrice = max;
        }
        result.sortBy = sortBy;
        return result;
    }, [filterValues, debouncedMinPrice, debouncedMaxPrice, sortBy]);

    const { data: products = [], isLoading: productsLoading, error: productsError, refetch: refetchProducts } = useProducts(filters);
    const { data: pickupPoints = [], isLoading: pickupPointsLoading } = usePickupPoints();
    const hasPickupPoints = pickupPoints.length > 0;
    const showEmptyPickupState =
        !pickupPointsLoading && !hasPickupPoints && !productsLoading && products.length === 0;
    const showMissingPickupAlert =
        !pickupPointsLoading && !hasPickupPoints && products.length > 0;

    // Фильтруем: «активные» = видны в каталоге; «скрытые» = всё остальное (скрыты / модерация / отклонены)
    const filteredProducts = useMemo(() => {
        const isCatalogVisible = (product: (typeof products)[number]) =>
            !product.isHidden && product.approved === true;

        let list = products;
        if (visibilityFilter === 'active') {
            list = list.filter(isCatalogVisible);
        } else if (visibilityFilter === 'hidden') {
            list = list.filter((product) => !isCatalogVisible(product));
        } else if (visibilityFilter === 'moderation') {
            list = list.filter((product) => product.approved === null || product.approved === undefined);
        } else if (visibilityFilter === 'rejected') {
            list = list.filter((product) => product.approved === false);
        }
        if (!debouncedSearchQuery.trim()) {
            return list;
        }
        const query = debouncedSearchQuery.toLowerCase().trim();
        const queryAsNum = parseInt(debouncedSearchQuery.trim(), 10);
        const isNumericQuery = !isNaN(queryAsNum) && String(queryAsNum) === debouncedSearchQuery.trim();
        return list.filter(product =>
            product.name.toLowerCase().includes(query) ||
            (isNumericQuery && product.id === queryAsNum) ||
            String(product.id).includes(debouncedSearchQuery.trim())
        );
    }, [products, debouncedSearchQuery, visibilityFilter]);

    const handleCreateProduct = () => {
        if (isMobile) {
            navigate('/products/new', { state: { from: '/products' } });
            return;
        }
        setIsCreateProductModalOpen(true);
    };

    const handleProductSelect = (productId: number, selected: boolean) => {
        setSelectedProductIds(prev => {
            const newSet = new Set(prev);
            if (selected) {
                newSet.add(productId);
            } else {
                newSet.delete(productId);
            }
            return newSet;
        });
    };

    const handleBulkActionComplete = () => {
        // Очищаем выбранные товары после выполнения действия
        setSelectedProductIds(new Set());
        // Выключаем режим выбора
        setIsSelectionMode(false);
    };

    const handleOpenAddToSetModal = (productIds: number[]) => {
        setModalProductIds(productIds);
        setIsAddToSetModalOpen(true);
    };

    const handleOpenAddToCategoryModal = (productIds: number[]) => {
        setModalProductIds(productIds);
        setIsAddToCategoryModalOpen(true);
    };

    const handleAddToSetSuccess = () => {
        // Очищаем выбранные товары после выполнения действия
        setSelectedProductIds(new Set());
        // Выключаем режим выбора
        setIsSelectionMode(false);
        // Очищаем ID товаров для модального окна
        setModalProductIds([]);
    };

    const handleAddToCategorySuccess = () => {
        // Очищаем выбранные товары после выполнения действия
        setSelectedProductIds(new Set());
        // Выключаем режим выбора
        setIsSelectionMode(false);
        // Очищаем ID товаров для модального окна
        setModalProductIds([]);
    };

    const handleCreateProductSuccess = () => {
        // Модалка закроется автоматически, список продуктов обновится через query invalidation
    };

    const handleEditProduct = (productId: number) => {
        if (isMobile) {
            navigate(`/products/${productId}/edit`, {
                state: { from: '/products' },
            });
            return;
        }
        setEditingProductId(productId);
        setIsEditProductModalOpen(true);
    };

    const handleEditProductSuccess = () => {
        // Модалка закроется автоматически, список продуктов обновится через query invalidation
        setEditingProductId(null);
    };


    return (
        <div className="page-container products-page">
            <Breadcrumbs items={[
                { label: 'Главная', path: '/' },
                { label: 'Товары' }
            ]} />

            <div className="page-header">
                <div className="page-header-top">
                    <div className="products-page-title-block">
                        <div className="products-page-title-row">
                            <h1>Товары</h1>
                            <QuietAddTrigger
                                onClick={handleCreateProduct}
                                disabled={pickupPointsLoading || !hasPickupPoints}
                                aria-label={
                                    pickupPointsLoading
                                        ? 'Загрузка…'
                                        : hasPickupPoints
                                            ? 'Создать товар'
                                            : 'Сначала добавьте точку самовывоза'
                                }
                                title={
                                    pickupPointsLoading || !hasPickupPoints
                                        ? 'Сначала добавьте точку самовывоза'
                                        : undefined
                                }
                            >
                                Добавить товар
                            </QuietAddTrigger>
                            <QuietAddTrigger
                                onClick={() => setIsImportModalOpen(true)}
                                disabled={pickupPointsLoading || !hasPickupPoints}
                                icon={<IoCloudUploadOutline size={16} aria-hidden />}
                                aria-label={
                                    pickupPointsLoading
                                        ? 'Загрузка…'
                                        : hasPickupPoints
                                            ? 'Импорт товаров из Excel'
                                            : 'Сначала добавьте точку самовывоза'
                                }
                                title={
                                    pickupPointsLoading || !hasPickupPoints
                                        ? 'Сначала добавьте точку самовывоза'
                                        : undefined
                                }
                            >
                                Импорт из Excel
                            </QuietAddTrigger>
                        </div>
                        {pickupPointsLoading ? (
                            <div className="products-page-pickup-status" aria-busy="true" aria-label="Загрузка точек самовывоза">
                                <Loader size="sm" />
                            </div>
                        ) : null}
                    </div>

                    {isSelectionMode ? (
                        <div className="products-page-header-actions">
                            <BulkActionsSelect
                                selectedProductIds={selectedProductIds}
                                onActionComplete={handleBulkActionComplete}
                                onOpenAddToSetModal={handleOpenAddToSetModal}
                                onOpenAddToCategoryModal={handleOpenAddToCategoryModal}
                            />
                            {/* <Button
                                variant={isSelectionMode ? 'secondary' : 'primary'}
                                onClick={handleToggleSelectionMode}
                            >
                                {isSelectionMode ? 'Отменить выбор' : 'Выбрать'}
                            </Button> */}
                        </div>
                    ) : null}
                </div>

                {showMissingPickupAlert ? (
                    <Alert
                        className="products-page-pickup-alert"
                        tone="error"
                        title="Нет точек самовывоза"
                    >
                        Без точки нельзя создать новый товар и показать товары покупателям в каталоге города.
                        <br />
                        <Link to="/contacts" className="products-page-pickup-alert__link">
                            К точкам самовывоза
                        </Link>
                    </Alert>
                ) : null}
            </div>

            {showEmptyPickupState ? (
                <div className="products-page-empty-pickup">
                    <div className="products-page-empty-pickup__card" role="status">
                        <div className="products-page-empty-pickup__icon" aria-hidden>
                            <IoLocationOutline size={28} />
                        </div>
                        <h2 className="products-page-empty-pickup__title">
                            Сначала добавьте точку самовывоза
                        </h2>
                        <p className="products-page-empty-pickup__text">
                            Без точки нельзя создать товар и показать его покупателям в каталоге города.
                        </p>
                        <div className="products-page-empty-pickup__actions">
                            <Button
                                className="products-page-empty-pickup__cta"
                                onClick={() => navigate('/contacts')}
                            >
                                К точкам самовывоза
                            </Button>
                            <Link to="/onboarding" className="products-page-empty-pickup__secondary">
                                Первые шаги
                            </Link>
                        </div>
                    </div>
                </div>
            ) : (
                <>
                    <div className="filters-section">
                        <div className="search-section-products">
                            <Input
                                className="products-page__search-input"
                                type="text"
                                placeholder="Поиск по названию или ID..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                leftAddon={<IoSearchOutline size={16} aria-hidden />}
                            />
                        </div>

                        {/* временно скрыто
                        <FiltersPopover
                            opened={filtersPopoverOpened}
                            onChange={setFiltersPopoverOpened}
                            filterValues={filterValues}
                            onFilterChange={setFilterValues}
                            onClear={handleClearFilters}
                            showCategoryFilter
                        />
                        */}

                        <div className="products-list-controls">
                            <QuietSelect
                                ariaLabel="Фильтр по видимости"
                                label={
                                    visibilityOptions.find((option) => option.value === visibilityFilter)?.label ??
                                    'Все товары'
                                }
                                value={visibilityFilter}
                                onChange={setVisibilityFilter}
                                options={visibilityOptions}
                            />
                            <QuietSelect
                                ariaLabel="Открыть сортировку"
                                label={sortOptions.find((option) => option.value === sortBy)?.label ?? 'Сортировка'}
                                value={sortBy}
                                onChange={setSortBy}
                                options={sortOptions}
                            />
                        </div>
                    </div>

                    <div className="content-wrapper">
                        <ProductList
                            products={filteredProducts}
                            isSelectionMode={isSelectionMode}
                            selectedProductIds={selectedProductIds}
                            onSelect={handleProductSelect}
                            isLoading={productsLoading || pickupPointsLoading}
                            error={productsError}
                            refetch={refetchProducts}
                            searchQuery={debouncedSearchQuery}
                            onCreateProduct={handleCreateProduct}
                            onEdit={handleEditProduct}
                            isCreateProductDisabled={pickupPointsLoading || !hasPickupPoints}
                            isCreateProductBusy={pickupPointsLoading}
                        />
                    </div>
                </>
            )}

            <AddToSetModal
                isOpen={isAddToSetModalOpen}
                onClose={() => {
                    setIsAddToSetModalOpen(false);
                    setModalProductIds([]);
                }}
                productIds={modalProductIds.length > 0 ? modalProductIds : Array.from(selectedProductIds)}
                onSuccess={handleAddToSetSuccess}
            />

            <AddToCategoryModal
                isOpen={isAddToCategoryModalOpen}
                onClose={() => {
                    setIsAddToCategoryModalOpen(false);
                    setModalProductIds([]);
                }}
                productIds={modalProductIds.length > 0 ? modalProductIds : Array.from(selectedProductIds)}
                onSuccess={handleAddToCategorySuccess}
            />

            <CreateProductModal
                isOpen={isCreateProductModalOpen}
                onClose={() => setIsCreateProductModalOpen(false)}
                onSuccess={handleCreateProductSuccess}
            />

            <ImportProductsModal
                isOpen={isImportModalOpen}
                onClose={() => setIsImportModalOpen(false)}
            />

            {editingProductId && (
                <EditProductModal
                    isOpen={isEditProductModalOpen}
                    onClose={() => {
                        setIsEditProductModalOpen(false);
                        setEditingProductId(null);
                    }}
                    productId={editingProductId}
                    onSuccess={handleEditProductSuccess}
                />
            )}
        </div>
    );
}

